import React, {createRef, useState, useContext} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import moment from "moment";
import MaterialTable from "@material-table/core";
import {TablePagination, Checkbox, Icon, useTheme} from "@material-ui/core";

import { useTripLegs, useUpdateTripLeg } from "../../../services/trips";
import TripMessaging from "./TripMessaging";

import {ColumnWidth} from "../../../blocks/ColumnHelper";
import AircraftFuelLogDialog from "../../Aircraft/components/AircraftFuelLogDialog";
import { defaultComponents } from '../../../controls/Material-Table-Components';
import { ComboSelect } from '../../../controls';
import MovementsTooltip from './TripListDialogs/MovementsTooltip';
import SystemContext from '../../../SystemContext';
import { getApiSitePrefix } from '../../../functions/SiteDefaults';

const dateFormat = "DD/MM/YYYY";

const formatChars={
  "U": "[A-Z]",
  "A": "[A-Z0-9]",
  "a": "[A-Za-z0-9]",
  "i": "[A-Za-z0-9\\-]",
  "9": "[0-9]",
  "D": "[0-9.]",
  "-": "-",
  "x": "."
}

const TripLegList = (_props) => {
  const queryClient = useQueryClient();
  const tableRef = createRef({state: {lastEditingRow: false}});
  const {isAuthenticated} = useAuth0();

  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const apiSitePrefix = getApiSitePrefix();

  const props = {
    ..._props
  }

  const {tripPrefix, tripNo} = props;

  const [state] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });

  const handleFuelLogDialogChange = (event) => {
    if (event.state === "cancel") {
      setAircraftFuelLogDialogProps ({
        ...aircraftFuelLogDialogProps,
        open: false,
      });
    }
    if (event.state === "save") {
      setAircraftFuelLogDialogProps ({
        ...aircraftFuelLogDialogProps,
        open: false,
      });
    }
  }

  const handleShowFuelLog = (e, rowData) => {
    setAircraftFuelLogDialogProps({
      ...aircraftFuelLogDialogProps,
      open: true,
      legRecord: rowData,
      onChange: handleFuelLogDialogChange,
      canEdit: state.canEdit
    })
  }

  const handleUpdateTripLeg = useUpdateTripLeg();

  const extraKeys = [props.tripUuid];

  const tripLegs = useTripLegs({tableRef, queryClient, extraKeys});


  const [aircraftFuelLogDialogProps, setAircraftFuelLogDialogProps] = useState({
    open: false,
    onChange: handleFuelLogDialogChange
  });

  const minDate = moment().add(1, 'day').format(dateFormat);
  const legTypes = ['FULL CHARTER', 'PART CHARTER', 'PART CHARTER WITH FREESALE', 'FREESALE', 'FERRY'];
  const transportTypes = ['PRIMARY', 'ONWARD', 'TRUCK'];

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: "never",
      emptyValue: "",
      initialEditValue: "",
    };

    return [
      {
        ...baseColumn,
        ...ColumnWidth(80,2,undefined,undefined,undefined,undefined,false),
        title: "Origin",
        field: "originAirportCode",
        editable: state.canEdit ? "onAdd" : "never",
        width: 70,
        maxWidth: 70,
        minWidth: 70,
        cellStyle: state.canEdit?{ width: 66, minWidth: 66, maxWidth: 66, padding: "8px 10px 8px 8px" }:{ width: 66, minWidth: 66, maxWidth: 66, padding: "8px 10px 8px 12px" },
        headerStyle: state.canEdit?{padding: "8px 2px" }:{padding: "8px 12px" },
      },
      {
        ...baseColumn,
        ...ColumnWidth(80,2,undefined,undefined,undefined,undefined,false),
        title: "Dest",
        field: "destinationAirportCode",
        width: 70,
        maxWidth: 70,
        minWidth: 70,
        cellStyle: state.canEdit?{ width: 66, minWidth: 66, maxWidth: 66, padding: "8px 2px" }:{ width: 66, minWidth: 66, maxWidth: 66, padding: "8px 12px" },
        headerStyle: state.canEdit?{padding: "8px 2px" }:{padding: "8px 12px" },
      },
      {
        ...baseColumn,
        title: "Alt",
        field: "alternateAirportCode",
        width: 70,
        maxWidth: 70,
        minWidth: 70,
        cellStyle: { width: 66, minWidth: 66, maxWidth: 66, padding: "8px 12px" },
        headerStyle: {padding: "8px 12px"},
      },
      {
        ...baseColumn,
        ...ColumnWidth(120,2,undefined,undefined,undefined,undefined,false),
        title: "Transport Type",
        field: "transportType",
        editable: props.canEdit ? "always" : "never",
        initialEditValue: transportTypes[0],
        render: (rowData) => (
          <ComboSelect
            id="transportType"
            group="TRANSPORT TYPES"
            subGroup=""
            fullWidth
            value={`${rowData.transportType}`}
            displayOnly={true}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(100,2,undefined,undefined,undefined,undefined,false),
        title: "Flight No",
        field: "flightNo",
        initialEditValue: "",
        render: (props) => (
          <div>
            {props.airlineCode}&nbsp;{props.flightNo}
          </div>
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(100,2,undefined,undefined,undefined,undefined,false),
        title: "Flight Date",
        field: "contractedDepartureTimeUTC",
        render: (rowData) => (
          <div>{moment(rowData.contractedDepartureTimeUTC).startOf('day').format('DD-MMM-YYYY')}</div>
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(90,2,undefined,undefined,undefined,undefined,false),
        title: "Departure UTC",
        field: "contractedDepartureTimeUTC",
        render: (props) => (
          <div>
            {props.contractedDepartureTimeUTC?moment.utc(props.contractedDepartureTimeUTC).format("HH:mm"):""}
          </div>
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(90,2,undefined,undefined,undefined,undefined,false),
        title: "Arrival UTC",
        field: "contractedArrivalTimeUTC",
        render: (props) => (
          <div>
            {props.contractedArrivalTimeUTC?moment.utc(props.contractedArrivalTimeUTC).format("HH:mm"):""}
          </div>
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(50, 2, undefined,undefined,undefined,undefined,false),
        title: "Times",
        field: "scheduledDepartureTimeUTC",
        editable: "never",
        render: (rowData) => {
          if (rowData.aircraftMovementUuid) {
            return (
              <MovementsTooltip aircraftMovementUuid={rowData.aircraftMovementUuid} tripLeg={rowData}>
                <Icon>access_time_filled</Icon>
              </MovementsTooltip>
            )
          } else if (rowData.estimatedDepartureTimeUTC || rowData.estimatedArrivalTimeUTC || rowData.scheduledDepartureTimeUTC != rowData.contractedDepartureTimeUTC || rowData.scheduledArrivalTimeUTC != rowData.contractedArrivalTimeUTC) {
            return (
              <MovementsTooltip aircraftMovementUuid={rowData.aircraftMovementUuid} tripLeg={rowData}>
                <Icon>access_time</Icon>
              </MovementsTooltip>
            )
          } else return "";
        }
      },
      {
        ...baseColumn,
        title: "Leg Type",
        field: "legType",
        render: (rowData) => (
          <ComboSelect
            id="legType"
            group="TRIP LEG TYPES"
            subGroup={rowData.transportType}
            fullWidth
            value={rowData.legType}
            displayOnly={true}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(80,2,undefined,undefined,undefined,undefined,false),
        cellStyle: { padding: "8px 2px 8px 2px" },
        headerStyle: { padding: "8px 2px 8px 2px" },
        title: "Aircraft",
        field: "aircraftId",
        width: 90,
        maxWidth: 90,
        minWidth: 90,
      },
      {
        ...baseColumn,
        ...ColumnWidth(80,2,undefined,undefined,undefined,undefined,false),
        title: "Tech Stop",
        field: "techStop",
        emptyValue: false,
        type: "boolean",
        render: rowData =>
          <Checkbox
            style={{padding: 0}}
            checked={rowData.techStop === 1 ?? true}
            value="true"
            //onChange={ checked => {props.onChange(checked ? 1 : 0)} }
          />
      },
      {
        ...baseColumn,
        ...ColumnWidth(80,2,undefined,undefined,undefined,undefined,false),
        title: "Loading Allowed",
        field: "allowLoading",
        emptyValue: false,
        type: "boolean",
        render: rowData =>
          <Checkbox
            style={{padding: 0}}
            checked={rowData.allowLoading === 1 ?? true}
            value="true"
            //onChange={ checked => {props.onChange(checked ? 1 : 0)} }
          />
      },
      {
        ...baseColumn,
        ...ColumnWidth(80,2,undefined,undefined,undefined,undefined,false),
        title: "Through Allowed",
        field: "allowThroughCargo",
        emptyValue: false,
        type: "boolean",
        render: rowData =>
          <Checkbox
            style={{padding: 0}}
            checked={rowData.allowThroughCargo === 1 ?? true}
            value="true"
            //onChange={ checked => {props.onChange(checked ? 1 : 0)} }
          />
      },

    ];
  };

  return (isAuthenticated &&
      <div style={{ marginLeft: 50, }}>
        <MaterialTable
            tableRef={tableRef}
            style={{
              backgroundColor: "#fdfdfd",
              border: "2px solid #aaaaaa",
              boxShadow: "none",
              borderRadius: 0,
            }}
            options={{
              actionsColumnIndex: -1,
              search: false,
              cellStyle: {
                padding: "8px 2px",
              },
              headerStyle: {
                backgroundColor: theme.palette.childTableHeader.backgroundColor,
                color: theme.palette.childTableHeader.textColor,
                padding: "2px",
              },
              toolbarButtonAlignment: "right",
              pageSize: 5,
              detailPanelType: "single",
            }}
            components={{
              ...defaultComponents,
              Pagination: (props) => (
                  <TablePagination
                      {...props}
                      rowsPerPageOptions={[5, 10, 15]}
                  />
              ),
            }}
            title="Trip Sectors"
            columns={getColumns({...props, canEdit: state.canEdit})}
            actions={[
              {
                icon: 'local_gas_station',
                tooltip: 'Display Fuel Log',
                iconProps: { style: { color: theme.palette.primary.main }},
                onClick: handleShowFuelLog
              }
            ]}
            detailPanel={
              [
                {
                  tooltip: "Show Sector Messaging",
                  icon: "notifications",
                  openIcon: "notifications_none",
                  render: (rowData) => {
                    return (
                      <div >
                        <div style={{ backgroundColor: "#f2f2f2" }}>
                          <TripMessaging
                            canEdit={state.canEdit}
                            listVariant={"LEG"}
                            tripUuid={rowData.tripUuid}
                            tripLegUuid={rowData.tripLegUuid}
                            route={rowData.originAirportCode + "-" + rowData.destinationAirportCode}
                          />
                        </div>
                      </div>
                    );
                  },
                },
              ]
            }
            data={tripLegs.data}
        />
        <AircraftFuelLogDialog
          {...aircraftFuelLogDialogProps}
        />
      </div>
  );
}

export default TripLegList;
